import {
  Platform,
  ShopMulticitySummarySuccessResultEnum,
} from "@b2bportal/air-shopping-api";
import type {
  MulticityRoute,
  ShopMulticitySummaryErrors,
  ShopMulticitySummaryRequest,
  ShopMulticitySummarySuccess,
} from "@b2bportal/air-shopping-api/lib/api";
import type { ITripTerminus } from "@b2bportal/core-types";

import { flightsShoppingApi, getDeviceType } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { selectShowAllAirCanadaPorterFares } from "@tenant/experiments";
import type { FlightState } from "../../../types";
import {
  filterLowestFaresForFixedPricingMulticity,
  transformFlightsToFourFareClasses,
} from "../utils";
import type { FetchFlightsRejectValue } from "./fetchFlights";

function shopSummaryErrorCodesFromMulticityErr(
  err: ShopMulticitySummaryErrors
): string[] {
  return [err.ShopMulticitySummaryErrors];
}

export const fetchMulticityFlights = createAsyncThunk<
  ShopMulticitySummarySuccess,
  { searchFixedPointsFares?: boolean; isFixedPointsPricingEligible?: boolean },
  { state: FlightState; rejectValue: FetchFlightsRejectValue }
>(
  "flightsShop/fetchMulticityFlights",
  async (
    { searchFixedPointsFares = false, isFixedPointsPricingEligible = false },
    thunkAPI
  ) => {
    const state = thunkAPI.getState();
    const {
      passengerCounts: {
        adultsCount,
        infantsInSeatCount,
        infantsOnLapCount,
        childrenCount,
      },
      multicityRoutes,
      tripFilter,
    } = state.flights.flightSearch;
    const { isDesktopAndUp } = getDeviceType();
    try {
      if (multicityRoutes == null || multicityRoutes.length === 0) {
        return thunkAPI.rejectWithValue({ error: "Missing required fields" });
      }

      const routes = multicityRoutes
        .filter(
          (
            route
          ): route is {
            origin: ITripTerminus;
            destination: ITripTerminus;
            departureDate: string;
          } =>
            route.origin != null &&
            route.destination != null &&
            route.departureDate != null
        )
        .map(
          (route): MulticityRoute => ({
            origin: route.origin?.id.code,
            destination: route.destination?.id.code,
            departureDate: route.departureDate,
          })
        );

      const requestBody: ShopMulticitySummaryRequest = {
        passengers: {
          ADT: Number(adultsCount),
          ...(infantsInSeatCount ? { INS: Number(infantsInSeatCount) } : {}),
          ...(infantsOnLapCount ? { INF: Number(infantsOnLapCount) } : {}),
          ...(childrenCount ? { CNN: Number(childrenCount) } : {}),
        },
        routes,
        tripFilter,
        platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
        searchFixedPointsFares,
      };

      const res =
        await flightsShoppingApi.apiV0ShopMulticitySummaryPost(requestBody);

      const data = res.data;

      switch (data.result.Result) {
        case ShopMulticitySummarySuccessResultEnum.Ok: {
          const fourFaresFlights = transformFlightsToFourFareClasses(
            data.result.value.flights
          );

          if (searchFixedPointsFares) {
            const showAllAirCanadaPorterFares =
              selectShowAllAirCanadaPorterFares(thunkAPI.getState());
            const filteredFlights = filterLowestFaresForFixedPricingMulticity(
              fourFaresFlights,
              showAllAirCanadaPorterFares
            );
            return {
              ...data.result.value,
              flights: filteredFlights,
            };
          }

          return {
            ...data.result.value,
            flights: fourFaresFlights,
          };
        }
        case ShopMulticitySummarySuccessResultEnum.Err: {
          const err = data.result.err;
          return thunkAPI.rejectWithValue({
            error: JSON.stringify(err),
            shopSummaryErrorCodes: shopSummaryErrorCodesFromMulticityErr(err),
          });
        }
      }
    } catch (error) {
      // Reject the thunk with an error message
      return thunkAPI.rejectWithValue({ error: String(error) });
    }
  }
);
